import { PatientSchema, TypeEnum } from "@api/src/types/few-schot-schema";
import { MageXPatient } from "@prisma/client";
import { Trans } from "@lingui/macro";
import ValueEditModal from "./ValueEditModal";
import { ValueCard } from "./ValueCard";
import { useState } from "react";
import { JsonArray, JsonObject } from "@prisma/client/runtime/library";
import { coerceToArray } from "@app/utils/resolve-field-value";
import { asTerms } from "@app/utils/problem-list";
import { valueLabel } from "@app/utils/value-labels";

interface ValueEnumArrayDisplayProps {
  aggregatedValue: string[] | null | undefined;
  correctedValue: string[] | null | undefined;
  property: keyof MageXPatient;
  type: TypeEnum;
  onlyTruth?: boolean;
  schema: PatientSchema;
  label: string;
  debug: boolean;
  hasValidatedTruth?: boolean;
  // Temporal projection page: render each array item as its own bordered card
  // (see ValueCard) instead of a comma-joined inline string. Off elsewhere.
  cardVariant?: boolean;
  setCorrectedValue: (
    value:
      | string
      | number
      | boolean
      | JsonArray
      | Date
      | JsonObject
      | null
      | undefined,
  ) => void;
}

const formatValue = (value: unknown): string | null => {
  if (value == null || value == undefined) return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return null;
      }
    }
    return JSON.stringify(value, null, 2);
  }
  return value.toString();
};

export default function ValueDisplayEnumArray({
  aggregatedValue: rawAggregatedValue,
  correctedValue: rawCorrectedValue,
  onlyTruth = false,
  type,
  schema,
  label,
  property,
  debug,
  setCorrectedValue,
  hasValidatedTruth = false,
  cardVariant = false,
}: ValueEnumArrayDisplayProps) {
  // Both props are typed ``string[]`` but can arrive as a bare string: a value
  // validated before the ontology retyped the field to array_* is still a
  // scalar in the append-only feedback log (and sometimes in the column). Left
  // uncoerced it either renders "Non déterminé" or throws on ``.map`` below.
  // A corrected problem list can also hold realities beside its labels: shown
  // by their labels.
  const correctedValue = asTerms(coerceToArray(rawCorrectedValue)) as
    | string[]
    | null
    | undefined;
  const aggregatedValue = asTerms(coerceToArray(rawAggregatedValue)) as
    | string[]
    | null
    | undefined;
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The values are the field's terms; they are SHOWN in the reader's language.
  const labels = schema[property]?.value_labels;
  const valueIsArray =
    (correctedValue &&
      Array.isArray(correctedValue) &&
      correctedValue.length > 0) ||
    (aggregatedValue &&
      Array.isArray(aggregatedValue) &&
      aggregatedValue.length > 0);
  const valueIsEnum =
    (valueIsArray && typeof correctedValue?.[0] === "string") ||
    typeof aggregatedValue?.[0] === "string";
  if (debug)
    console.warn({
      valueIsEnum,
      valueIsArray,
      aggregatedValue,
      correctedValue,
      property,
      type,
    });

  if (!valueIsEnum) {
    if (cardVariant) {
      return (
        <ValueCard muted>
          <Trans>Non déterminé</Trans>
        </ValueCard>
      );
    }
    return (
      <div className="relative group flex flex-col items-end gap-0.5">
        <span className="font-medium text-right block text-slate-400 italic">
          <Trans>Non déterminé</Trans>
        </span>
      </div>
    );
  }

  const formattedTruthValue = formatValue(correctedValue);
  if (onlyTruth) {
    let isValid = true;
    if (
      correctedValue !== undefined &&
      correctedValue !== null &&
      Array.isArray(correctedValue)
    ) {
      if (type === "array_enum") {
        const enumValues = schema[property].items?.enum;
        if (enumValues) {
          isValid = correctedValue.every((item) => enumValues.includes(item));
        }
      }
      const value = correctedValue
        .map((item) => valueLabel(labels, item as string))
        .join(", ");
      if (!isValid || isModalOpen) {
        return (
          <div className="flex justify-center items-center bg-black h-full">
            <button
              type="button"
              className="bg-black text-white flex w-full text-center flex-wrap overflow-hidden"
              onClick={() => setIsModalOpen(true)}
            >
              {value}
            </button>

            <ValueEditModal
              open={isModalOpen}
              setIsOpen={setIsModalOpen}
              label={label}
              property={property}
              type={type}
              debug={debug}
              schema={schema}
              aggregatedValue={correctedValue}
              correctedValue={correctedValue}
              setCorrectedValue={setCorrectedValue}
              isNotStatued={true}
              calledFrom="ValueDisplayEnumArray empty"
            />
          </div>
        );
      }
      return value;
    }
    if (schema[property].enum) {
      const enumValues = schema[property].enum;
      if (enumValues) {
        isValid = enumValues.includes(formattedTruthValue as string);
      }
    }
    if (!isValid || isModalOpen) {
      return (
        <div className="flex justify-center items-center bg-black h-full">
          <button
            type="button"
            className="bg-black text-white flex w-full text-center flex-wrap overflow-hidden"
            onClick={() => setIsModalOpen(true)}
          >
            {formattedTruthValue}
          </button>

          <ValueEditModal
            open={isModalOpen}
            setIsOpen={setIsModalOpen}
            label={label}
            property={property}
            type={type}
            debug={debug}
            schema={schema}
            aggregatedValue={correctedValue}
            correctedValue={correctedValue}
            setCorrectedValue={setCorrectedValue}
            isNotStatued={true}
            calledFrom="ValueDisplayEnumArray not empty"
          />
        </div>
      );
    }
    return formattedTruthValue?.trim() ? (
      <span>{formattedTruthValue.trim()}</span>
    ) : (
      <span className="text-slate-400 italic"><Trans>Non déterminé</Trans></span>
    );
  }

  // Truth (correctedValue) wins over aggregated when set — a pencil-saved or
  // "Retenir"-validated value is the user's authoritative answer. When the
  // user has explicitly validated this field (``hasValidatedTruth``) the
  // truth is authoritative even if empty, so we must NOT fall back to the
  // aggregated value — the user's "Confirmer l'absence de valeur" choice has
  // to render as "Non déterminé" with the green check.
  const displayValues = hasValidatedTruth
    ? correctedValue
    : (correctedValue?.length ? correctedValue : aggregatedValue);

  const formattedDisplay =
    displayValues
      ?.map((item) => valueLabel(labels, item as string))
      .join(", ") ?? "";

  const hasValues = !!displayValues?.length;

  if (cardVariant) {
    if (!hasValues) {
      return (
        <ValueCard muted>
          <Trans>Non déterminé</Trans>
        </ValueCard>
      );
    }
    // Agnostic array rendering: one card per item (never a comma-joined line),
    // matching the synthesised problem list. Fixes every flat array field —
    // diagnostics_principaux (brut), niveaux_operes, surgical_measures_*, etc.
    return (
      <div className="flex w-full min-w-0 flex-col gap-2">
        {displayValues?.map((item, i) => (
          <ValueCard key={i}>{valueLabel(labels, String(item))}</ValueCard>
        ))}
      </div>
    );
  }

  return (
    <div className="relative group flex flex-col items-end gap-0.5">
      <span
        className={[
          "font-medium text-right block",
          !hasValues ? "text-slate-400 italic" : "text-slate-900",
        ].join(" ")}
      >
        {hasValues ? formattedDisplay : <Trans>Non déterminé</Trans>}
      </span>
    </div>
  );
}
